import React, { useState, useEffect } from 'react';
import { ArrowLeft, Video, FileText, Download, Play, Eye, Calendar, User, Search, Filter, Upload, Settings } from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import axios from 'axios';

const TrainingMaterials = () => {
  const [activeTab, setActiveTab] = useState('videos');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [isAdmin, setIsAdmin] = useState(false); // This would come from user context
  
  // API data state
  const [trainingMaterials, setTrainingMaterials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    fetchTrainingMaterials();
  }, []);

  useEffect(() => {
    fetchTrainingMaterials();
  }, [searchTerm, filterCategory]);

  const fetchTrainingMaterials = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (filterCategory && filterCategory !== 'all') params.category = filterCategory;
      
      const response = await axios.get(`${API_BASE}/api/training/materials`, { params });
      setTrainingMaterials(response.data.materials || []);
    } catch (error) {
      console.error('Error fetching training materials:', error);
      setError('Failed to load training materials');
      setTrainingMaterials([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Get unique categories from API data
  const categories = ['all', ...new Set(trainingMaterials.map(material => material.category))];

  // Filter materials by type and current tab
  const filteredVideos = trainingMaterials.filter(material => 
    material.type === 'video'
  );

  const filteredDocuments = trainingMaterials.filter(material => 
    material.type === 'document'
  );

  // Since API handles filtering, we just need to separate by type
  // The search and category filtering is done server-side

  return (
    <div className="dark-container min-h-screen" style={{ paddingTop: '100px' }}>
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Back Button */}
        <Link to="/community">
          <Button className="btn-secondary mb-8">
            <ArrowLeft size={20} />
            Back to Community
          </Button>
        </Link>

        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="display-large mb-6">
            Training <span className="turquoise-text-gradient">Materials</span>
          </h1>
          <p className="body-large text-gray-300 max-w-3xl mx-auto">
            Access comprehensive training resources, video courses, and documentation to enhance your business excellence expertise.
          </p>
        </div>

        {/* Search and Filter Bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="flex-1 relative">
            <Search size={20} className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search training materials..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400"
            />
          </div>
          
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white min-w-48"
          >
            {categories.map(category => (
              <option key={category} value={category}>
                {category === 'all' ? 'All Categories' : category}
              </option>
            ))}
          </select>

          {isAdmin && (
            <Button className="btn-primary-enhanced">
              <Upload size={20} />
              Upload Material
            </Button>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-4 mb-8 border-b border-gray-800">
          <button
            onClick={() => setActiveTab('videos')}
            className={`px-6 py-3 font-medium transition-all duration-200 border-b-2 ${
              activeTab === 'videos'
                ? 'text-turquoise-400 border-turquoise-400'
                : 'text-gray-400 border-transparent hover:text-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Video size={20} />
              <span>Training Videos ({filteredVideos.length})</span>
            </div>
          </button>
          
          <button
            onClick={() => setActiveTab('documents')}
            className={`px-6 py-3 font-medium transition-all duration-200 border-b-2 ${
              activeTab === 'documents'
                ? 'text-turquoise-400 border-turquoise-400'
                : 'text-gray-400 border-transparent hover:text-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <FileText size={20} />
              <span>Documents ({filteredDocuments.length})</span>
            </div>
          </button>
        </div>

        {/* Content Area */}
        {isLoading ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 border-4 border-turquoise-500/30 border-t-turquoise-500 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-400">Loading training materials...</p>
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Settings size={48} className="text-red-400" />
            </div>
            <h3 className="heading-3 text-white mb-4">Error Loading Materials</h3>
            <p className="body-medium text-gray-400 max-w-md mx-auto mb-6">{error}</p>
            <Button onClick={fetchTrainingMaterials} className="btn-primary">
              Try Again
            </Button>
          </div>
        ) : activeTab === 'videos' ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredVideos.map((video) => (
              <div key={video.id} className="bg-gray-900/50 border border-turquoise-500/20 rounded-16 p-6 hover:border-turquoise-500/40 transition-all duration-300">
                <div className="aspect-video bg-gray-800 rounded-lg mb-4 flex items-center justify-center text-4xl relative">
                  {video.thumbnail}
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                    <Play size={48} className="text-turquoise-400" />
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <h3 className="heading-4 text-white line-clamp-2">{video.title}</h3>
                    <p className="text-sm text-gray-400 line-clamp-2 mt-2">{video.description}</p>
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <div className="flex items-center gap-4">
                      <span className="flex items-center gap-1">
                        <Calendar size={12} />
                        {new Date(video.uploadDate).toLocaleDateString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <Eye size={12} />
                        {video.views} views
                      </span>
                    </div>
                    <span className="px-2 py-1 bg-turquoise-500/20 text-turquoise-400 rounded text-xs">
                      {video.category}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between pt-3 border-t border-gray-800">
                    <div className="flex items-center gap-2">
                      <User size={14} className="text-gray-400" />
                      <span className="text-xs text-gray-400">{video.uploader}</span>
                    </div>
                    <span className="text-sm font-medium text-turquoise-400">{video.duration}</span>
                  </div>
                  
                  <Button className="btn-primary-enhanced w-full">
                    <Play size={16} />
                    Watch Now
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredDocuments.map((doc) => (
              <div key={doc.id} className="bg-gray-900/50 border border-turquoise-500/20 rounded-16 p-6 hover:border-turquoise-500/40 transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 bg-turquoise-500/20 rounded-lg flex items-center justify-center text-2xl flex-shrink-0">
                    {doc.thumbnail || '📄'}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="heading-4 text-white mb-2">{doc.title}</h3>
                        <p className="text-sm text-gray-400 line-clamp-2">{doc.description}</p>
                      </div>
                      <span className="px-3 py-1 bg-turquoise-500/20 text-turquoise-400 rounded-full text-xs font-medium ml-4">
                        {doc.category}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-6 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <FileText size={12} />
                          {doc.type} • {doc.duration || 'Document'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar size={12} />
                          {new Date(doc.uploadDate).toLocaleDateString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye size={12} />
                          {doc.views || 0} views
                        </span>
                        <span className="flex items-center gap-1">
                          <User size={12} />
                          {doc.uploader}
                        </span>
                      </div>
                      
                      <Button className="btn-secondary">
                        <Download size={16} />
                        Download
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && ((activeTab === 'videos' && filteredVideos.length === 0) || 
          (activeTab === 'documents' && filteredDocuments.length === 0)) && (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-turquoise-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              {activeTab === 'videos' ? <Video size={48} className="text-turquoise-400" /> : <FileText size={48} className="text-turquoise-400" />}
            </div>
            <h3 className="heading-3 text-white mb-4">No {activeTab} found</h3>
            <p className="body-medium text-gray-400 max-w-md mx-auto">
              {searchTerm || filterCategory !== 'all' 
                ? 'Try adjusting your search or filter criteria'
                : `No ${activeTab} have been uploaded yet.`
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrainingMaterials;